/**
 * Os documentos do caso com as páginas (E139.F3, ADR-0095 §2.6–§2.7): cada arquivo recebido, a
 * contagem por situação e cada página, com o documento em que caiu, a origem do texto e a
 * situação. Toda página pode ser aberta.
 */
import { Button } from '@/components/ui';
import {
  contagemDasPaginas,
  paginasPorArquivo,
  type ArquivoRecebido,
  type PaginaDaFichaData,
  type PaginaNaFonte,
} from '@/lib/ficha_da_leitura';

import { preencher, t } from './textos';

const ATENCAO = new Set([
  'por_ler',
  'nada_lido',
  'leitura_com_falha',
  'nao_processada',
  'sem_documento',
]);

function OndeCaiu({ pagina }: { pagina: PaginaDaFichaData }): JSX.Element {
  if (pagina.documento_nome === null || pagina.pagina === null) {
    return <span className="text-fg-muted">{t.documentos.foraDeDocumento}</span>;
  }
  return (
    <span className="text-fg-muted">
      {preencher(t.documentos.noDocumento, {
        documento: pagina.documento_nome,
        pagina: pagina.pagina,
      })}
    </span>
  );
}

function Pagina({
  arquivo,
  pagina,
  onVerNaFonte,
}: {
  arquivo: ArquivoRecebido;
  pagina: PaginaDaFichaData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
}): JSX.Element {
  const numero = pagina.pagina_no_original;
  return (
    <li
      className="flex flex-wrap items-baseline gap-x-sc-2 text-body-sm"
      data-testid="ficha-pagina"
      data-situacao={pagina.situacao}
    >
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={() =>
          onVerNaFonte({ documentoId: arquivo.originalId, pagina: numero, titulo: arquivo.nome })
        }
        aria-label={preencher(t.verPagina, { pagina: numero, documento: arquivo.nome })}
      >
        {preencher(t.documentos.pagina, { n: numero })}
      </Button>
      <span className={ATENCAO.has(pagina.situacao) ? 'text-warning-700' : 'text-fg'}>
        {t.situacaoDaPagina[pagina.situacao]}
        {pagina.situacao === 'lida' ? ` (${t.textoDaPagina[pagina.texto]})` : ''}
      </span>
      <OndeCaiu pagina={pagina} />
    </li>
  );
}

function Arquivo({
  arquivo,
  onVerNaFonte,
}: {
  arquivo: ArquivoRecebido;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
}): JSX.Element {
  const contagem = contagemDasPaginas(arquivo.paginas)
    .map((c) => `${c.total} ${t.situacaoDaPagina[c.situacao].toLowerCase()}`)
    .join(' · ');
  return (
    <li className="border border-border bg-surface px-sc-3 py-sc-2" data-testid="ficha-arquivo">
      <p className="font-semibold text-fg">{arquivo.nome}</p>
      <p className="text-body-sm text-fg-muted">
        {preencher(t.documentos.paginas, { n: arquivo.paginas.length })} · {contagem}
      </p>
      <ul className="mt-sc-2 space-y-0.5">
        {arquivo.paginas.map((pagina) => (
          <Pagina
            key={pagina.pagina_no_original}
            arquivo={arquivo}
            pagina={pagina}
            onVerNaFonte={onVerNaFonte}
          />
        ))}
      </ul>
    </li>
  );
}

export function DocumentosDaFicha({
  paginas,
  onVerNaFonte,
}: {
  paginas: readonly PaginaDaFichaData[];
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
}): JSX.Element {
  const arquivos = paginasPorArquivo(paginas);
  return (
    <div data-testid="ficha-documentos">
      <h4 className="text-body-md font-semibold text-fg">{t.documentos.titulo}</h4>
      <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.documentos.subtitulo}</p>
      {arquivos.length === 0 ? (
        <p className="mt-sc-2 text-body-sm text-fg-muted">{t.documentos.semPaginas}</p>
      ) : (
        <ul className="mt-sc-2 space-y-sc-2">
          {arquivos.map((arquivo) => (
            <Arquivo key={arquivo.originalId} arquivo={arquivo} onVerNaFonte={onVerNaFonte} />
          ))}
        </ul>
      )}
    </div>
  );
}
